/**
 * A QR code for the room's join link, for the Board at the front of the room
 * and the host's own screen. Phones in the room scan it; the address is printed
 * beside it for anyone who would rather type.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

export interface JoinQrProps {
  url: string;
  /** Pixel size of the square. */
  size?: number;
  /** Dark and light module colors; defaults draw black on white, readable on any board. */
  dark?: string;
  light?: string;
  className?: string;
}

export function JoinQr({ url, size = 220, dark = '#000000', light = '#ffffff', className }: JoinQrProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    QRCode.toDataURL(url, { margin: 1, width: size, color: { dark, light }, errorCorrectionLevel: 'M' })
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, size, dark, light]);

  if (failed) return null;
  return (
    <img
      src={src ?? undefined}
      width={size}
      height={size}
      alt={`QR code for ${url}`}
      data-testid="join-qr"
      className={className}
      style={{ display: 'block', width: size, height: size, background: light, imageRendering: 'pixelated' }}
    />
  );
}
